import { featureFlags } from "@/config/featureFlags"
import type { TFunction } from "i18next"

export type ContextMenuCommand =
  | "preview"
  | "savePdf"
  | "previewAll"
  | "saveAllPdf"
  | "addToList"
  | "duplicate"
  | "rename"
  | "delete"
  | "deleteSelected"
  | "previewSelected"
  | "saveSelectedPdf"
  | "duplicateSelected"
  | "expandAll"
  | "collapseAll"
  | "duplicateItem"
  | "renameItem"
  | "deleteItem"
  | "configureDelivery"

export interface ContextMenuItem {
  label: string
  command: ContextMenuCommand
  separator?: boolean
  destructive?: boolean
}

// Report List context menu (right-click on tab header or ellipsis menu)
export function getReportListMenuItems(t: TFunction): ContextMenuItem[] {
  return [
    { label: t("contextMenu.previewAll"), command: "previewAll" },
    { label: t("contextMenu.saveAllPdf"), command: "saveAllPdf" },
    { label: "", command: "previewAll", separator: true },
    ...(featureFlags.enableDeliveryConfig
      ? [
          { label: t("contextMenu.configureDelivery"), command: "configureDelivery" as const },
          { label: "", command: "configureDelivery" as const, separator: true },
        ]
      : []),
    { label: t("contextMenu.duplicate"), command: "duplicate" },
    { label: t("contextMenu.rename"), command: "rename" },
    { label: t("contextMenu.delete"), command: "delete", destructive: true },
    { label: "", command: "deleteSelected", separator: true },
    { label: t("contextMenu.deleteSelected"), command: "deleteSelected", destructive: true },
  ]
}

// Report List Item context menu (right-click on grid row)
export function getReportListItemMenuItems(t: TFunction): ContextMenuItem[] {
  return [
    { label: t("contextMenu.preview"), command: "preview" },
    { label: t("contextMenu.savePdf"), command: "savePdf" },
    { label: "", command: "preview", separator: true },
    { label: t("contextMenu.duplicate"), command: "duplicateItem" },
    { label: t("contextMenu.rename"), command: "renameItem" },
    { label: "", command: "renameItem", separator: true },
    { label: t("contextMenu.delete"), command: "deleteItem", destructive: true },
  ]
}

// Report List Item context menu when the right-clicked row's checkbox is checked;
// applies to all checked rows in the grid, not just the row that was right-clicked
export function getSelectedReportListItemMenuItems(t: TFunction): ContextMenuItem[] {
  return [
    { label: t("contextMenu.preview"), command: "previewSelected" },
    { label: t("contextMenu.savePdf"), command: "saveSelectedPdf" },
    { label: "", command: "previewSelected", separator: true },
    { label: t("contextMenu.duplicate"), command: "duplicateSelected" },
    { label: "", command: "duplicateSelected", separator: true },
    { label: t("contextMenu.delete"), command: "deleteSelected", destructive: true },
  ]
}

// Report List Item ellipsis menu (••• button on grid row)
export function getReportListItemEllipsisMenuItems(t: TFunction): ContextMenuItem[] {
  return [
    { label: t("contextMenu.duplicate"), command: "duplicateItem" },
    { label: t("contextMenu.rename"), command: "renameItem" },
    { label: "", command: "renameItem", separator: true },
    { label: t("contextMenu.delete"), command: "deleteItem", destructive: true },
  ]
}

// Report node context menu (right-click on report in library tree)
export function getReportMenuItems(t: TFunction): ContextMenuItem[] {
  return [
    { label: t("contextMenu.preview"), command: "preview" },
    { label: t("contextMenu.savePdf"), command: "savePdf" },
    { label: t("contextMenu.addToList"), command: "addToList" },
  ]
}

// Folder node context menu (right-click on folder in library tree)
export function getFolderMenuItems(t: TFunction): ContextMenuItem[] {
  return [
    { label: t("contextMenu.expandAll"), command: "expandAll" },
    { label: t("contextMenu.collapseAll"), command: "collapseAll" },
  ]
}

// Legacy exports for backward compatibility (deprecated)
// These use English defaults until all consumers are migrated to the function versions
export const reportListMenuItems: ContextMenuItem[] = [
  { label: "Preview All", command: "previewAll" },
  { label: "Save All to PDF", command: "saveAllPdf" },
  { label: "", command: "previewAll", separator: true },
  ...(featureFlags.enableDeliveryConfig
    ? [
        { label: "Configure Delivery...", command: "configureDelivery" as const },
        { label: "", command: "configureDelivery" as const, separator: true },
      ]
    : []),
  { label: "Duplicate", command: "duplicate" },
  { label: "Rename", command: "rename" },
  { label: "Delete", command: "delete", destructive: true },
  { label: "", command: "deleteSelected", separator: true },
  { label: "Delete Selected Items", command: "deleteSelected", destructive: true },
]

export const reportListItemMenuItems: ContextMenuItem[] = [
  { label: "Preview", command: "preview" },
  { label: "Save to PDF", command: "savePdf" },
  { label: "", command: "preview", separator: true },
  { label: "Duplicate", command: "duplicateItem" },
  { label: "Rename", command: "renameItem" },
  { label: "Delete", command: "deleteItem", destructive: true },
]

export const reportListItemEllipsisMenuItems: ContextMenuItem[] = [
  { label: "Duplicate", command: "duplicateItem" },
  { label: "Rename", command: "renameItem" },
  { label: "", command: "renameItem", separator: true },
  { label: "Delete", command: "deleteItem", destructive: true },
]

export const reportMenuItems: ContextMenuItem[] = [
  { label: "Preview", command: "preview" },
  { label: "Save to PDF", command: "savePdf" },
  { label: "Add to List", command: "addToList" },
]

export const folderMenuItems: ContextMenuItem[] = [
  { label: "Expand All", command: "expandAll" },
  { label: "Collapse All", command: "collapseAll" },
]
